/**
 * Chips (docs/design/MASTER.md sections 3.3 and 6).
 *
 *  - Chip: a static label. 32px tall, 13px sentence-case text.
 *  - ChipButton: a filter or choice. 44px tall on phones, 36px from 640px. Pass `pressed` for a toggle.
 *  - RemovableChip: an applied filter with an x button whose hit area is 44px on phones.
 *
 * Tones carry meaning, never decoration: positive (green: human evidence, a check passed), caution (amber),
 * unknown (grey: not studied, no data) and neutral. A status tone always shows a dot or an icon next to its
 * word, so colour is never the only signal. For evidence tiers use the tier styles in src/lib/stackEvidence.ts.
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

export type ChipTone = "neutral" | "positive" | "caution" | "unknown";

const TONE: Record<ChipTone, { chip: string; dot: string }> = {
  neutral: { chip: "border-border bg-secondary text-secondary-foreground", dot: "bg-muted-foreground" },
  positive: { chip: "border-success/30 bg-success/10 text-success", dot: "bg-success" },
  caution: { chip: "border-warning/30 bg-warning/10 text-warning", dot: "bg-warning" },
  unknown: { chip: "border-border bg-secondary text-muted-foreground", dot: "bg-transparent ring-1 ring-inset ring-muted-foreground" },
};

const BASE = "inline-flex max-w-full items-center gap-1.5 rounded-full border type-label";

function Lead({ tone, icon, dot }: { tone: ChipTone; icon?: ReactNode; dot?: boolean }) {
  if (icon) return <span className="inline-flex shrink-0 [&_svg]:h-4 [&_svg]:w-4" aria-hidden>{icon}</span>;
  // Status tones get a dot by default; neutral chips only when asked.
  const showDot = dot ?? tone !== "neutral";
  if (!showDot) return null;
  return <span aria-hidden className={cn("h-2 w-2 shrink-0 rounded-full", TONE[tone].dot)} />;
}

export interface ChipProps {
  tone?: ChipTone;
  /** A lucide icon element in place of the dot, e.g. <FlaskConical />. */
  icon?: ReactNode;
  /** Show or hide the leading dot. Default: shown for positive, caution and unknown. */
  dot?: boolean;
  className?: string;
  children: ReactNode;
}

/** A static label: an evidence word, a status, a category. */
export function Chip({ tone = "neutral", icon, dot, className, children }: ChipProps) {
  return (
    <span className={cn(BASE, "min-h-8 px-3 py-1", TONE[tone].chip, className)}>
      <Lead tone={tone} icon={icon} dot={dot} />
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
}

export interface ChipButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  tone?: ChipTone;
  icon?: ReactNode;
  dot?: boolean;
  /** For a toggle (a filter that is on or off). Sets aria-pressed and the selected look. */
  pressed?: boolean;
  children: ReactNode;
}

/** A filter or choice chip. 44px tall on phones. */
export const ChipButton = forwardRef<HTMLButtonElement, ChipButtonProps>(function ChipButton(
  { tone = "neutral", icon, dot, pressed, className, children, type = "button", ...props },
  ref,
) {
  const selected = pressed === true;
  return (
    <button
      ref={ref}
      type={type}
      aria-pressed={pressed === undefined ? undefined : selected}
      className={cn(
        BASE,
        "min-h-11 cursor-pointer px-4 transition-colors duration-150 sm:min-h-9",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        "disabled:pointer-events-none disabled:opacity-50",
        selected ? "border-primary bg-primary/10 text-foreground" : cn(TONE[tone].chip, "hover:border-foreground/30"),
        className,
      )}
      {...props}
    >
      {selected ? (
        <Check aria-hidden className="h-4 w-4 shrink-0 text-primary" />
      ) : (
        <Lead tone={tone} icon={icon} dot={dot} />
      )}
      <span className="min-w-0 truncate">{children}</span>
    </button>
  );
});

export interface RemovableChipProps {
  tone?: ChipTone;
  /** The filter's text. Also used in the remove button's label: "Remove Recovery". */
  children: string;
  onRemove: () => void;
  /** Override the remove button's accessible name. */
  removeLabel?: string;
  className?: string;
}

/** An applied filter. The x button is 44px square on phones and 36px from 640px. */
export function RemovableChip({ tone = "neutral", children, onRemove, removeLabel, className }: RemovableChipProps) {
  return (
    <span className={cn(BASE, "min-h-11 pl-4 sm:min-h-9", TONE[tone].chip, className)}>
      <span className="min-w-0 truncate">{children}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel ?? `Remove ${children}`}
        className="-ml-1 inline-flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-150 hover:bg-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:h-9 sm:w-9"
      >
        <X aria-hidden className="h-4 w-4" />
      </button>
    </span>
  );
}
